<script setup>
/**
 * The numbers test mode is allowed to reach — add one, take one off, nothing else.
 *
 * NOT `AllowlistEditor.vue`, which is the other list-shaped control in this tree and is a
 * different thing entirely: that one ticks names off a catalogue the server hands us, and
 * its whole design is about what to do when that catalogue cannot be read. Here there is
 * no catalogue. The owner types a phone number that exists nowhere until she types it, so
 * there is nothing to fetch, nothing to go stale, and no fifth state.
 *
 * THE NUMBER IS NORMALISED HERE, THE SAME WAY THE SERVER NORMALISES IT.
 * `outbound/phone.ts` strips spaces, brackets, hyphens and dots, folds Arabic-Indic and
 * Extended Arabic-Indic digits to ASCII, and then demands `+[1-9]` followed by 1-14
 * digits — it never repairs a number into something merely plausible. Doing the same here
 * buys two things a bare regex would not: an owner on an Arabic keyboard is not told her
 * own number is invalid, and the duplicate check compares the stored spelling rather than
 * the typed one, so `+965 5000 1234` cannot be added a second time as `+96550001234`.
 *
 * THE DRAFT IS CLEARED BY THE ANSWER, NOT BY THE CLICK. Every write on this page is
 * PATCH-then-re-render, so a failed save must leave the typed number where the owner can
 * press Add again — clearing the box on submit would silently eat it.
 */
import { computed, ref, watch } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';

import WasloInput from '../WasloInput.vue';
import { useWasloI18n } from '../../i18n.js';

const props = defineProps({
  /** The stored list, straight off the settings DTO. */
  numbers: { type: Array, default: () => [] },
  /**
   * How many may be saved — `settings.limits.maxTestNumbers`, which is the sidecar's own
   * `MAX_ALLOWLIST` projected through the BFF. REQUIRED, and deliberately not defaulted:
   * a copy of that constant living here is a number that drifts silently, and the day it
   * drifts the owner gets a 400 from a button this component told her was still live.
   */
  maxNumbers: { type: Number, required: true },
  /** True while the page is writing this list. */
  isSaving: { type: Boolean, default: false },
  /** True when test mode is on, which is when an empty list means "nobody". */
  isLive: { type: Boolean, default: false },
  /** A message from the last failed write. */
  saveError: { type: String, default: '' },
  /**
   * Which i18n block the words come from. The outbound page's by default; the practice
   * readiness path (P-5.2) points it at `WASLO.READINESS.TESTERS`, whose eleven keys are the
   * same names. The list is the same kind of thing on both pages (who a test may reach); the
   * sentences are not, so they are never shared.
   */
  copy: { type: String, default: 'WASLO.OUTBOUND.SETUP.TEST' },
});

const emit = defineEmits(['add', 'remove']);

const SEPARATORS = /[\s()\-.]/gu;
const E164 = /^\+[1-9]\d{1,14}$/u;
const ARABIC_INDIC = 0x0660;
const EXTENDED_ARABIC_INDIC = 0x06f0;

const asciiDigits = value =>
  [...value]
    .map(character => {
      const point = character.codePointAt(0);
      if (point === undefined) return character;
      if (point >= ARABIC_INDIC && point <= ARABIC_INDIC + 9) {
        return String(point - ARABIC_INDIC);
      }
      if (
        point >= EXTENDED_ARABIC_INDIC &&
        point <= EXTENDED_ARABIC_INDIC + 9
      ) {
        return String(point - EXTENDED_ARABIC_INDIC);
      }
      return character;
    })
    .join('');

/** @returns {?string} The stored spelling, or null when the server would refuse it. */
const normalise = raw => {
  const cleaned = asciiDigits(String(raw)).replace(SEPARATORS, '');
  return E164.test(cleaned) ? cleaned : null;
};

const { t } = useWasloI18n();
const key = name => `${props.copy}.${name}`;

const draft = ref('');
/** The normalised number we asked the server to add, until it comes back in the list. */
const pending = ref(null);
const localError = ref('');

const isFull = computed(() => props.numbers.length >= props.maxNumbers);

watch(
  () => props.numbers,
  next => {
    if (pending.value !== null && next.includes(pending.value)) {
      pending.value = null;
      draft.value = '';
    }
  }
);

const add = () => {
  if (props.isSaving || isFull.value) return;
  localError.value = '';

  const number = normalise(draft.value);
  if (number === null) {
    localError.value = t(key('INVALID'));
    return;
  }
  if (props.numbers.includes(number)) {
    localError.value = t(key('DUPLICATE'));
    return;
  }

  pending.value = number;
  emit('add', number);
};

const remove = number => {
  if (props.isSaving) return;
  localError.value = '';
  emit('remove', number);
};
</script>

<template>
  <div class="flex flex-col gap-4" data-waslo-outbound-test-numbers>
    <div class="flex flex-col gap-1">
      <h4 class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t(key('NUMBERS_HEADING')) }}
      </h4>
      <p class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11">
        {{ t(key('NUMBERS_DESCRIPTION')) }}
      </p>
    </div>

    <!--
      Amber only while test mode is actually ON. An empty list under a switch that is off
      is somebody about to set this up, not a fault — and an amber sentence there would be
      the status colour that fires when nothing is wrong (README §4).
    -->
    <p
      v-if="numbers.length === 0"
      class="mb-0 text-sm leading-6"
      :class="isLive ? 'text-n-amber-11' : 'text-n-slate-11'"
      data-waslo-outbound-test-numbers-empty
    >
      {{ t(key('EMPTY')) }}
    </p>

    <ul v-else class="flex flex-col gap-2 mb-0 list-none">
      <li
        v-for="number in numbers"
        :key="number"
        class="flex items-center justify-between gap-3 px-3 py-2 outline outline-1 -outline-offset-1 outline-n-weak rounded-lg bg-n-solid-1"
        data-waslo-outbound-test-number
      >
        <!--
          `<bdi>` and `min-w-0`: a `+965…` number sitting in an Arabic paragraph drags its
          leading `+` to the wrong end of the run without an isolate (theming rule R7's
          reason, in the one place on this card that renders something a customer typed).
        -->
        <span class="min-w-0 text-sm break-all text-n-slate-12">
          <bdi>{{ number }}</bdi>
        </span>
        <button
          type="button"
          class="grid rounded-md shrink-0 place-items-center size-7 text-n-slate-11 hover:text-n-ruby-9 hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="isSaving"
          :aria-label="`${t(key('REMOVE'))} ${number}`"
          :title="t(key('REMOVE'))"
          data-waslo-outbound-test-number-remove
          @click="remove(number)"
        >
          <span class="size-4 i-lucide-trash-2" aria-hidden="true" />
        </button>
      </li>
    </ul>

    <!--
      README §3: an `<input>` on `flex: 1` beside a fixed sibling collapsed to 23px in
      three separate incidents. The field gets `flex-1 min-w-0` and the button `shrink-0`,
      and below `sm` they are stacked rather than shrunk.
    -->
    <div class="flex flex-col gap-3 sm:flex-row sm:items-end">
      <WasloInput
        v-model="draft"
        class="flex-1 min-w-0"
        type="tel"
        :label="t(key('NUMBER_LABEL'))"
        :placeholder="t(key('PLACEHOLDER'))"
        :hint="t(key('HINT'))"
        :disabled="isSaving || isFull"
        data-waslo-outbound-test-number-input
      />
      <!--
        THIS PAGE'S OWN KEY, not the Do not contact page's `NUMBERS.ADD.ACTION`. Sharing
        one string across two screens looks like thrift and is a tripwire: the two lists
        mean opposite things (one is who you MAY message while testing, the other is who
        you must NEVER message), so the day either page's copy is tuned, the other one
        changes with it and nobody edited it.
      -->
      <Button
        class="shrink-0"
        :label="t(key('ADD'))"
        icon="i-lucide-plus"
        variant="faded"
        color="slate"
        size="sm"
        :is-loading="isSaving && pending !== null"
        :disabled="isSaving || isFull"
        data-waslo-outbound-test-number-add
        @click="add"
      />
    </div>

    <p v-if="isFull" class="mb-0 text-sm text-n-slate-11">
      {{ t(key('FULL')) }}
    </p>

    <p
      v-if="localError || saveError"
      class="mb-0 text-sm text-n-ruby-9"
      data-waslo-outbound-test-numbers-error
    >
      {{ localError || saveError }}
    </p>
  </div>
</template>
